import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { Filter } from '../components/universal-search-bar';

/**
 * Component state for {@link UniversalFilterStore}.
 *
 * @remarks
 * Filters are stored as an NgRx `EntityState` so lookups by logical key
 * (`field.id` + `operator` + `value`, see {@link filterEntityId}) stay O(1).
 * The UI still consumes an ordered `Filter[]` via {@link UniversalFilterStore.filters$}
 */
export interface UniversalFilterStoreState {
  /** Entity collection of active search filters. */
  filters: EntityState<Filter>;
}

const getEntityId = (filter: Filter) => [filter.field.id, filter.operator, filter.value].join('-');

const filterAdapter = createEntityAdapter<Filter>({
  selectId: getEntityId,
});

const { selectAll } = filterAdapter.getSelectors();

const initialFiltersState = filterAdapter.getInitialState();

function replaceOrAppendFilter(state: EntityState<Filter>, filter: Filter): EntityState<Filter> {
  const cleared = filterAdapter.removeOne(getEntityId(filter), state);
  return filterAdapter.addOne(filter, cleared);
}

/**
 * NgRx ComponentStore that holds universal search / table filters and an optional saved-config label.
 *
 * @remarks
 * - Mutations run through {@link ComponentStore} updaters; subscribe to {@link filters$} for reactive lists.
 * - {@link addFilter} and {@link upsertFilter} share the same implementation: any existing row with the same
 *   entity id is removed, then the row is appended so it appears last (legacy upsert ordering).
 * - For bulk edits (e.g. drop every “label” clause then add one), combine {@link getFilters} with
 *   {@link setFilters} in the caller.
 */
@Injectable()
export class UniversalFilterStore extends ComponentStore<UniversalFilterStoreState> {
  constructor() {
    super({
      filters: initialFiltersState,
    });
  }

  /**
   * Emits the current filters as an ordered array whenever the entity collection changes.
   *
   * @remarks Order follows NgRx entity `ids` (insertion / `setAll` order).
   */
  public readonly filters$ = this.select((state) => selectAll(state.filters));

  /**
   * Replaces the entire filter list and normalizes entity state in one update.
   *
   * @param filters - New ordered list; becomes the single source of truth for the entity collection.
   */
  public readonly setFilters = this.updater((state, filters: Filter[]) => ({
    ...state,
    filters: filterAdapter.setAll(filters, state.filters),
  }));

  /**
   * Appends a filter after removing any existing row with the same field, operator, and value.
   *
   * @remarks Equivalent to {@link upsertFilter}; at most one row exists per {@link filterEntityId}.
   */
  public readonly addFilter = this.updater((state, filter: Filter) => ({
    ...state,
    filters: replaceOrAppendFilter(state.filters, filter),
  }));

  /**
   * Inserts or refreshes a filter: removes the previous row with the same entity id, then appends the new row.
   *
   * @param filter - Row to place at the end of the ordered list.
   */
  public readonly upsertFilter = this.updater((state, filter: Filter) => ({
    ...state,
    filters: replaceOrAppendFilter(state.filters, filter),
  }));

  /**
   * Removes the filter whose field id, operator, and value match the given row.
   *
   * @param filter - A filter instance used only for identity; need not be the same object reference as stored.
   */
  public readonly removeFilter = this.updater((state, filter: Filter) => ({
    ...state,
    filters: filterAdapter.removeOne(getEntityId(filter), state.filters),
  }));

  /**
   * Clears all filters and the saved configuration name.
   */
  public readonly clearFilters = this.updater((state) => ({
    ...state,
    filters: filterAdapter.getInitialState(),
  }));

  public readonly mergeFiltersPreserving = this.updater(
    (state, params: { predicate: (f: Filter) => boolean; newTrailing: Filter[] }) => {
      const preservedFilters = selectAll(state.filters).filter(params.predicate);
      const newFilters = [...preservedFilters, ...params.newTrailing];

      return {
        ...state,
        filters: filterAdapter.setAll(newFilters, state.filters),
      };
    }
  );
}
